import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

// ── Props ─────────────────────────────────────────────────────────────────────

/**
 * Standalone mode: renders the gate UI directly (no children).
 * Use with an early-return pattern: if (!gate.allowed) return <HardGate ... />
 */
type StandaloneProps = {
  feature: string
  unlockDescription: string
  locked?: never
  featureName?: never
  children?: never
}

/**
 * Wrapper mode: conditionally shows children or the gate overlay.
 * Use when you want to keep the surrounding layout and swap the content.
 */
type WrapperProps = {
  locked: boolean
  featureName: string
  children: ReactNode
  feature?: never
  unlockDescription?: never
}

type HardGateProps = StandaloneProps | WrapperProps

// ── Gate UI ───────────────────────────────────────────────────────────────────

function GateUI({ title, description }: { title: string; description: string }) {
  return (
    <div
      role="region"
      aria-label={`${title} requires Premium`}
      className="flex flex-col items-center justify-center text-center py-16 px-6 gap-5"
    >
      {/* Lock icon */}
      <div className="w-14 h-14 rounded-2xl bg-violet-deep/40 border border-violet/20 flex items-center justify-center">
        <svg
          className="w-6 h-6 text-violet-glow"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      </div>

      <div className="max-w-sm">
        <h3 className="text-star-white font-semibold text-base mb-2">{title}</h3>
        <p className="text-star-soft text-sm leading-relaxed">{description}</p>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link
          to="/pricing"
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium text-sm hover:shadow-glow-violet transition-all"
        >
          Start free 7-day trial
        </Link>
        <Link
          to="/pricing"
          className="text-sm text-star-dim hover:text-star-soft transition-colors"
        >
          View all plans →
        </Link>
      </div>

      <p className="text-star-dim text-xs">No credit card required for the trial.</p>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * HardGate blocks access to premium features.
 *
 * **Standalone mode** — place after an early-return guard:
 * ```tsx
 * if (!gate.isLoading && !gate.allowed) {
 *   return <HardGate feature="Remedies" unlockDescription="..." />
 * }
 * ```
 *
 * **Wrapper mode** — wraps existing content and swaps it with the gate when locked:
 * ```tsx
 * <HardGate locked={!gate.allowed} featureName="Kundali Matching">
 *   <MyContent />
 * </HardGate>
 * ```
 */
export function HardGate(props: HardGateProps) {
  // Wrapper mode
  if ('locked' in props) {
    const { locked, featureName, children } = props
    if (!locked) return <>{children}</>
    return (
      <GateUI
        title={featureName}
        description={`${featureName} is a Premium feature. Start your free 7-day trial to unlock it.`}
      />
    )
  }

  // Standalone mode
  return <GateUI title={props.feature} description={props.unlockDescription} />
}
